import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Grid,
  MenuItem,
  Select,
  CircularProgress,
  Alert,
  IconButton,
  tableCellClasses,
  Autocomplete,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import styled_ from '@emotion/styled';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchItemPurchaseSummary,
  downloadItemPurchaseSummary
} from '../../store/reportStore/reportThunk';
import { fetchSuppliers } from '../../store/supplierStore/supplierThunk';
import { fetchInventories } from '../../store/inventoryStore/inventoryThunk';
import { ItemPurchaseSummaryItem, ItemPurchaseSummaryFetch } from '../../utils/report.types';
import { ApiStatus } from '../../utils/api.types';
import { toast } from 'react-toastify';
import { Utils } from '../../utils/common-utils';
import ReusableReportHeader from './ReusableReportHeader';
import moment from 'moment';

// Styled components - GSTR-2 Purchase Summary Style
const StyledTableCell = styled_(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#eaecf0',
    color: '#000',
    border: '0.75px solid #d9d4d4',
    margin: 0,
    padding: 5,
    fontSize: '9pt',
    paddingLeft: '15px !important',
  },
  [`&.${tableCellClasses.footer}`]: {
    backgroundColor: '#f5f5f5',
    color: 'black',
    borderTop: '0.25px solid #d9d4d4',
    borderBottom: '0.25px solid #d9d4d4',
    left: 0,
    bottom: 0,
    zIndex: 2,
    position: 'sticky'
  },
  [`&.${tableCellClasses.body}`]: {
    verticalAlign: 'super',
    fontSize: '9pt',
    fontWeight: 'normal',
    color: '#292d35',
    padding: 5,
    border: '0.75px solid #d9d4d4',
    paddingLeft: '15px !important',
    margin: 0
  },
}));

const StyledTableRow = styled_(TableRow)(({ theme }) => ({
  // Using the same empty style as GSTR-2 report
}));

interface ItemPurchaseSummaryProps {
  type: string;
  rawData?: any;
}

const ItemPurchaseSummary: React.FC<ItemPurchaseSummaryProps> = ({ type, rawData }) => {
  const dispatch = useAppDispatch();
  const { status } = useAppSelector(state => state.reports);
  const { selectedAccount } = useAppSelector(state => state.auth);
  const { suppliers } = useAppSelector(state => state.suppliers);
  const { inventories } = useAppSelector(state => state.inventories);

  // Get financial year dates
  const { startDateFY, endDateFY } = Utils.getFinancialYearDates();

  // State management
  const [reportData, setReportData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDateRange, setSelectedDateRange] = useState('This FY Year (Apr - Mar)');
  const [dateRange, setDateRange] = useState({
    startDate: startDateFY,
    endDate: endDateFY
  });
  const [searchSupplier, setSearchSupplier] = useState<any>(null);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [sortBy, setSortBy] = useState('lastPurchaseDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Prepare supplier options for search
  const supplierOptions = useMemo(() => {
    return (suppliers || []).map((supplier: any) => ({
      _id: supplier._id,
      name: supplier.name
    }));
  }, [suppliers]);

  // Prepare item options
  const itemOptions = useMemo(() => {
    return (inventories || []).map((sku: any) => ({
      _id: sku._id,
      name: sku.name,
      code: sku.itemCode
    }));
  }, [inventories]);

  // Handle date range change
  const handleDateRangeChange = (event: any) => {
    const value = event.target.value;
    setSelectedDateRange(value);

    let newStartDate: Date;
    let newEndDate: Date;

    switch (value) {
      case 'Today':
        newStartDate = new Date();
        newEndDate = new Date();
        break;
      case 'Yesterday':
        newStartDate = new Date(Date.now() - 1 * 24 * 60 * 60 * 1000);
        newEndDate = new Date(Date.now() - 1 * 24 * 60 * 60 * 1000);
        break;
      case 'This Week':
        newStartDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        newEndDate = new Date();
        break;
      case 'Last Week':
        newStartDate = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000);
        newEndDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'This Month':
        const thisMonthDate = new Date();
        newStartDate = new Date(thisMonthDate.getFullYear(), thisMonthDate.getMonth(), 1);
        newEndDate = new Date(thisMonthDate.getFullYear(), thisMonthDate.getMonth() + 1, 0);
        break;
      case 'Last Month':
        const lastMonthDate = new Date();
        newStartDate = new Date(lastMonthDate.getFullYear(), lastMonthDate.getMonth() - 1, 1);
        newEndDate = new Date(lastMonthDate.getFullYear(), lastMonthDate.getMonth(), 0);
        break;
      case 'FY Q1 (Apr - Jun)':
        newStartDate = new Date(startDateFY.getFullYear(), 3, 1); // April
        newEndDate = new Date(startDateFY.getFullYear(), 6, 0); // June end
        break;
      case 'FY Q2 (Jul - Sept)':
        newStartDate = new Date(startDateFY.getFullYear(), 6, 1); // July
        newEndDate = new Date(startDateFY.getFullYear(), 9, 0); // Sept end
        break;
      case 'FY Q3 (Oct - Dec)':
        newStartDate = new Date(startDateFY.getFullYear(), 9, 1); // October
        newEndDate = new Date(startDateFY.getFullYear(), 12, 0); // Dec end
        break;
      case 'FY Q4 (Jan - Mar)':
        newStartDate = new Date(endDateFY.getFullYear(), 0, 1); // January
        newEndDate = new Date(endDateFY.getFullYear(), 3, 0); // March end
        break;
      case 'This FY Year (Apr - Mar)':
        newStartDate = startDateFY;
        newEndDate = endDateFY;
        break;
      case 'Last FY Year (Apr - Mar)':
        const { startDateLastFY, endDateLastFY } = Utils.getLastFinancialYearDates();
        newStartDate = startDateLastFY;
        newEndDate = endDateLastFY;
        break;
      default:
        newStartDate = startDateFY;
        newEndDate = endDateFY;
    }

    setDateRange({
      startDate: newStartDate,
      endDate: newEndDate
    });
  };

  // Fetch data function
  const fetchData = useCallback(async () => {
    if (!selectedAccount?._id) return;

    setIsLoading(true);

    const params: ItemPurchaseSummaryFetch = {
      accountId: selectedAccount._id,
      supplierId: searchSupplier?._id || undefined,
      itemId: selectedItem?._id || undefined,
      startDate: moment(dateRange.startDate).format('YYYY-MM-DD'),
      endDate: moment(dateRange.endDate).format('YYYY-MM-DD'),
      sortBy,
      sortOrder
    };

    console.log('Fetching with params:', params);

    try {
      const response = await dispatch(fetchItemPurchaseSummary(params)).unwrap();
      setReportData(response);
      console.log('Response received:', response);
    } catch (error: any) {
      console.error('Fetch error:', error);
      toast.error(error.message || 'Failed to fetch item purchase summary');
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, selectedAccount, searchSupplier, selectedItem, dateRange, sortBy, sortOrder]);

  // Fetch suppliers and inventories on mount
  useEffect(() => {
    if (selectedAccount?._id) {
      dispatch(fetchSuppliers({ accountId: selectedAccount._id }));
      dispatch(fetchInventories({ accountId: selectedAccount._id, type: 'all' }));
    }
  }, [dispatch, selectedAccount]);

  // Load data on component mount or filter changes
  useEffect(() => {
    if (rawData) {
      setReportData(rawData);
      setIsLoading(false);
    } else {
      fetchData();
    }
  }, [rawData, fetchData]);

  // Handle download functions
  const handleDownloadPDF = async () => {
    if (!selectedAccount?._id) return;

    const params: ItemPurchaseSummaryFetch = {
      accountId: selectedAccount._id,
      supplierId: searchSupplier?._id || undefined,
      itemId: selectedItem?._id || undefined,
      startDate: moment(dateRange.startDate).format('YYYY-MM-DD'),
      endDate: moment(dateRange.endDate).format('YYYY-MM-DD'),
      sortBy,
      sortOrder,
      isDownloadPdf: 'true'
    };

    try {
      const response = await dispatch(downloadItemPurchaseSummary(params)).unwrap();
      if (response) {
        const blob = new Blob([response], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `item_purchase_summary_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("PDF download completed");
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to download PDF');
    }
  };

  const handleDownloadExcel = async () => {
    if (!selectedAccount?._id) return;

    const params: ItemPurchaseSummaryFetch = {
      accountId: selectedAccount._id,
      supplierId: searchSupplier?._id || undefined,
      itemId: selectedItem?._id || undefined,
      startDate: moment(dateRange.startDate).format('YYYY-MM-DD'),
      endDate: moment(dateRange.endDate).format('YYYY-MM-DD'),
      sortBy,
      sortOrder,
      isDownloadExcel: 'true'
    };

    try {
      const response = await dispatch(downloadItemPurchaseSummary(params)).unwrap();
      if (response) {
        const blob = new Blob([response], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `item_purchase_summary_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Excel download completed");
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to download Excel');
    }
  };

  // Handle filter reset
  const handleRefreshFilters = () => {
    setSearchSupplier(null);
    setSelectedItem(null);
    setSelectedDateRange('This FY Year (Apr - Mar)');
    setDateRange({
      startDate: startDateFY,
      endDate: endDateFY
    });
    setSortBy('lastPurchaseDate');
    setSortOrder('desc');
  };

  return (
    <Box sx={{ width: 'calc(100% - 7px)', overflow: 'hidden', mb: 2 }} className={'c-card2'}>
      {!isLoading && status !== ApiStatus.LOADING ? (
        <>
          {/* Report Header */}
          <ReusableReportHeader
            reportType="ITEM_PURCHASE_SUMMARY"
            title="Item Purchase Summary"
            subtitle="Summary of purchase transactions grouped by supplier and item"
            recordCount={reportData?.data?.summary?.totalRecords || 0}
            isLoading={isLoading}
            onDownloadPDF={handleDownloadPDF}
            onDownloadExcel={handleDownloadExcel}
            showPrint={false}
            showShare={false}
          />

          <div style={{ marginBottom: 12 }} id='pdfContainer'>
            {/* Header and Filters Section */}
            <Grid container p={2} style={{ borderBottom: '.5px solid #9da7b7', marginBottom: 0 }}>
              <Grid xs={12} style={{ textAlign: 'left' }}>

                {/* Filters */}
                <Box style={{ display: 'flex', alignItems: 'center', width: '100%', flexWrap: 'wrap', gap: '10px' }}>
                  <IconButton onClick={handleRefreshFilters}
                    style={{ marginRight: '5px', color: '#488dff', backgroundColor: '#fff', border: '3px solid #e9ecf1' }}
                    size='small' ><RefreshIcon /></IconButton>

                  

                  {/* Search Supplier Autocomplete */}
                  <Autocomplete
                    disablePortal
                    id="search-supplier"
                    value={searchSupplier || null}
                    style={{ width: 250, marginRight: '10px' }}
                    onChange={(event, value) => setSearchSupplier(value)}
                    options={supplierOptions}
                    getOptionLabel={(option) => option.name || ''}
                    renderInput={(params) => <TextField {...params}
                      variant="standard"
                      name="supplier"
                      size='small'
                      placeholder="Type here to search supplier..."
                      InputProps={{ ...params.InputProps, disableUnderline: true }}
                      className='c-text-field-3'
                    />}
                  />

                  {/* Search Item Autocomplete */}
                  <Autocomplete
                    disablePortal
                    id="search-item"
                    value={selectedItem || null}
                    style={{ width: 250, marginRight: '10px' }}
                    onChange={(event, value) => setSelectedItem(value)}
                    options={itemOptions}
                    getOptionLabel={(option) => option.name || ''}
                    renderInput={(params) => <TextField {...params}
                      variant="standard"
                      name="item"
                      size='small'
                      placeholder="Type here to search items..."
                      InputProps={{ ...params.InputProps, disableUnderline: true }}
                      className='c-text-field-3'
                    />}
                  />

                  {/* Date Range Select */}
                  <Select
                    name="dateRange"
                    disableUnderline
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          border: "1.5px solid #e9ecf1",
                          boxShadow: 'none',
                          borderRadius: '5px'
                        }
                      }
                    }}
                    value={selectedDateRange}
                    onChange={handleDateRangeChange}
                    sx={{
                      boxShadow: "none",
                      width: 220,
                      marginRight: '10px',
                      '& .MuiOutlinedInput-input': { p: 0, m: 0 },
                      '& .MuiSelect-select': { p: 0 },
                      ".MuiOutlinedInput-notchedOutline": { border: 0 },
                      "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { border: 0 },
                      "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { border: 0 },
                    }}
                    className='c-text-field-3'
                    variant="outlined"
                  >
                    <MenuItem value="Select Date">Select Date</MenuItem>
                    <MenuItem value="Today">Today</MenuItem>
                    <MenuItem value="Yesterday">Yesterday</MenuItem>
                    <MenuItem value="This Week">This Week</MenuItem>
                    <MenuItem value="Last Week">Last Week</MenuItem>
                    <MenuItem value="This Month">This Month</MenuItem>
                    <MenuItem value="Last Month">Last Month</MenuItem>
                    <MenuItem value="FY Q1 (Apr - Jun)">FY Q1 (Apr - Jun)</MenuItem>
                    <MenuItem value="FY Q2 (Jul - Sept)">FY Q2 (Jul - Sept)</MenuItem>
                    <MenuItem value="FY Q3 (Oct - Dec)">FY Q3 (Oct - Dec)</MenuItem>
                    <MenuItem value="FY Q4 (Jan - Mar)">FY Q4 (Jan - Mar)</MenuItem>
                    <MenuItem value="This FY Year (Apr - Mar)">This FY Year (Apr - Mar)</MenuItem>
                    <MenuItem value="Last FY Year (Apr - Mar)">Last FY Year (Apr - Mar)</MenuItem>
                  </Select>
                </Box>
              </Grid>
            </Grid>

          {/* Data Table */}
              <Table stickyHeader sx={{ minWidth: 700, marginBottom: '100px' }} size='small' aria-label="customized table">
            <TableHead>
              <TableRow>
                <StyledTableCell width={100}>Purchase Date</StyledTableCell>
                <StyledTableCell width={200}>Supplier Name</StyledTableCell>
                <StyledTableCell width={200}>Item Name (Item Code)</StyledTableCell>
                <StyledTableCell align="right" width={80}>Total Qty</StyledTableCell>
                <StyledTableCell align="right" width={100}>Rate per Item</StyledTableCell>
                <StyledTableCell align="right" width={80}>Tax Rate (%)</StyledTableCell>
                <StyledTableCell align="right" width={100}>Tax Amount</StyledTableCell>
                <StyledTableCell align="right" width={120}>Additional Charges</StyledTableCell>
                <StyledTableCell align="right" width={100}>Total Value</StyledTableCell>
                <StyledTableCell align="right" width={100}>No. of Invoices</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <StyledTableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Loading...
                    </Typography>
                  </StyledTableCell>
                </TableRow>
              ) : (reportData?.data?.items?.length) ? (
                <>
                  {reportData.data.items.map((item: ItemPurchaseSummaryItem) => (
                    <StyledTableRow key={item._id}>
                      <StyledTableCell>
                        {item.lastPurchaseDate
                          ? moment(item.lastPurchaseDate).format('DD/MM/YYYY')
                          : 'N/A'
                        }
                      </StyledTableCell>
                      <StyledTableCell>{item.supplierName}</StyledTableCell>
                      <StyledTableCell>
                        {item.itemName}
                        {item.itemCode && ` (${item.itemCode})`}
                      </StyledTableCell>
                      <StyledTableCell align="right">{Utils.formatNumber(item.totalQty || 0, false, false)}</StyledTableCell>
                      <StyledTableCell align="right">{Utils.formatNumber(item.ratePerItem || 0, false, true)}</StyledTableCell>
                      <StyledTableCell align="right">{item.taxRate ? `${Utils.formatNumber(item.taxRate, false, false)}%` : '0%'}</StyledTableCell>
                      <StyledTableCell align="right">{Utils.formatNumber(item.taxAmount || 0, false, true)}</StyledTableCell>
                      <StyledTableCell align="right">{Utils.formatNumber(item.additionalCharges || 0, false, true)}</StyledTableCell>
                      <StyledTableCell align="right">{Utils.formatNumber(item.totalValue || 0, false, true)}</StyledTableCell>
                      <StyledTableCell align="right">{item.invoiceCount || 0}</StyledTableCell>
                    </StyledTableRow>
                  ))}
                  {/* Summary Row */}
                  <StyledTableRow>
                    <StyledTableCell sx={{ backgroundColor: '#f5f5f5' }}></StyledTableCell>
                    <StyledTableCell colSpan={2} sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                      TOTAL
                    </StyledTableCell>
                    <StyledTableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                      {Utils.formatNumber(reportData.data.summary.totalQty || 0, false, false)}
                    </StyledTableCell>
                    <StyledTableCell sx={{ backgroundColor: '#f5f5f5' }}></StyledTableCell>
                    <StyledTableCell sx={{ backgroundColor: '#f5f5f5' }}></StyledTableCell>
                    <StyledTableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                      {Utils.formatNumber(reportData.data.summary.totalTaxAmount || 0, false, true)}
                    </StyledTableCell>
                    <StyledTableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                      {Utils.formatNumber(reportData.data.summary.totalAdditionalCharges || 0, false, true)}
                    </StyledTableCell>
                    <StyledTableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                      {Utils.formatNumber(reportData.data.summary.totalValue || 0, false, true)}
                    </StyledTableCell>
                    <StyledTableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                      {reportData.data.summary.totalInvoices || 0}
                    </StyledTableCell>
                  </StyledTableRow>
                </>
              ) : (
                <TableRow>
                  <StyledTableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No records found
                    </Typography>
                  </StyledTableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          </div>
        </>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          {status === ApiStatus.FAILED ? (
            <Alert severity="error" sx={{ m: 2 }}>
              Failed to load item purchase summary. Please try again.
            </Alert>
          ) : (
            <CircularProgress color="info" />
          )}
        </Box>
      )}
    </Box>
  );
};

export default ItemPurchaseSummary;
